export interface DeviceInfoSession {
  role: string | null;
  roleId: number | null;
  projectId: number | null;
  projectName: string | null;
}

export interface DeviceInfoInteraction {
  type: string;
  target?: string;
  timestamp: number;
}

interface ParsedBrowser {
  name: string | null;
  version: string | null;
  engine: string | null;
}

interface ParsedOs {
  name: string | null;
  version: string | null;
}

function parseBrowser(userAgent: string): ParsedBrowser {
  let name: string | null = null;
  let version: string | null = null;
  let engine: string | null = null;

  if (/Edg\/([\d.]+)/i.test(userAgent)) {
    name = 'Edge';
    version = RegExp.$1 || null;
  } else if (/OPR\/([\d.]+)/i.test(userAgent) || /Opera\/([\d.]+)/i.test(userAgent)) {
    name = 'Opera';
    version = RegExp.$1 || null;
  } else if (/SamsungBrowser\/([\d.]+)/i.test(userAgent)) {
    name = 'Samsung Browser';
    version = RegExp.$1 || null;
  } else if (/Firefox\/([\d.]+)/i.test(userAgent)) {
    name = 'Firefox';
    version = RegExp.$1 || null;
  } else if (/Chrome\/([\d.]+)/i.test(userAgent)) {
    name = 'Chrome';
    version = RegExp.$1 || null;
  } else if (/Safari\/([\d.]+)/i.test(userAgent) && /Version\/([\d.]+)/i.test(userAgent)) {
    name = 'Safari';
    version = RegExp.$1 || null;
  } else if (/MSIE ([\d.]+)/i.test(userAgent) || /Trident\/.*rv:([\d.]+)/i.test(userAgent)) {
    name = 'Internet Explorer';
    version = RegExp.$1 || null;
  }

  if (/Gecko\/([\d.]+)/i.test(userAgent)) {
    engine = 'Gecko';
  } else if (/AppleWebKit\/([\d.]+)/i.test(userAgent)) {
    engine = 'WebKit';
  } else if (/Blink/i.test(userAgent)) {
    engine = 'Blink';
  }

  return { name, version, engine };
}

function parseOs(userAgent: string): ParsedOs {
  let name: string | null = null;
  let version: string | null = null;

  if (/Windows NT ([\d.]+)/i.test(userAgent)) {
    name = 'Windows';
    version = RegExp.$1 || null;
  } else if (/Mac OS X ([\d_]+)/i.test(userAgent)) {
    name = 'macOS';
    version = RegExp.$1.replace(/_/g, '.') || null;
  } else if (/Android ([\d.]+)/i.test(userAgent)) {
    name = 'Android';
    version = RegExp.$1 || null;
  } else if (/iPhone OS ([\d_]+)/i.test(userAgent) || /iPad; CPU OS ([\d_]+)/i.test(userAgent)) {
    name = 'iOS';
    version = RegExp.$1.replace(/_/g, '.') || null;
  } else if (/Linux/i.test(userAgent)) {
    name = 'Linux';
  }

  return { name, version };
}

interface NetworkConnectionInfo {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

function getNetworkInfo(): {
  online: boolean;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
} {
  const connection =
    (navigator as unknown as { connection?: NetworkConnectionInfo }).connection ||
    (navigator as unknown as { mozConnection?: NetworkConnectionInfo }).mozConnection ||
    (navigator as unknown as { webkitConnection?: NetworkConnectionInfo }).webkitConnection ||
    undefined;

  return {
    online: navigator.onLine,
    effectiveType: (connection && (connection as { effectiveType?: string }).effectiveType) || null,
    downlink: (connection && (connection as { downlink?: number }).downlink) ?? null,
    rtt: (connection && (connection as { rtt?: number }).rtt) ?? null,
  };
}

function getScreenInfo(): {
  width: number;
  height: number;
  availWidth: number;
  availHeight: number;
  devicePixelRatio: number;
  orientation: string | null;
  colorDepth: number;
} {
  const orientation =
    (screen.orientation && (screen.orientation as { type?: string }).type) ||
    null;
  return {
    width: screen.width,
    height: screen.height,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    orientation,
    colorDepth: screen.colorDepth,
  };
}

async function getServiceWorkerState(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const active = registration.active;
    return active ? active.state : registration.installing?.state || registration.waiting?.state || 'unknown';
  } catch {
    return 'error';
  }
}

export function collectDeviceInfo(
  routePath: string,
  pageUrl: string,
  session: DeviceInfoSession,
  recentInteractions: DeviceInfoInteraction[]
): Record<string, unknown> {
  const browser = parseBrowser(navigator.userAgent);
  const os = parseOs(navigator.userAgent);

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: navigator.languages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: getScreenInfo(),
    browser,
    os,
    network: getNetworkInfo(),
    appState: {
      routePath,
      pageUrl,
      visibilityState: document.visibilityState,
      serviceWorkerState: null, // async; updated below if available
      buildTime: document.querySelector('meta[name="build-time"]')?.getAttribute('content') || null,
      baseUrl: import.meta.env.BASE_URL,
    },
    session,
    recentInteractions: recentInteractions.slice(-10),
  };
}

export async function enrichDeviceInfoWithServiceWorker(
  info: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const appState = info.appState as Record<string, unknown> | undefined;
  if (appState) {
    appState.serviceWorkerState = await getServiceWorkerState();
  }
  return info;
}
